/**
 * Small data layer over SWR.
 *
 * The hook shape here (`useQuery`, `useMutation`, `queryOptions`, an
 * `invalidateQueries` client) is deliberately the one the app's 12 data call
 * sites already use, so the migration off the previous query library touched
 * the plumbing rather than every screen. SWR does the real work: caching,
 * request dedupe, revalidation.
 *
 * Two differences from what it replaced, both intentional:
 *   - `data` is `T | undefined`, not narrowed by `isPending`. Callers must
 *     check it before use; the compiler enforces that.
 *   - `queryFn` gets no AbortSignal — SWR has no cancellation model. In-flight
 *     requests for unmounted components resolve and are discarded.
 */

import { useCallback, useRef, useState } from "react";
import useSWR, { useSWRConfig, type SWRConfiguration } from "swr";

export interface QueryOptions<T> {
  /** Serialisable cache key. Arrays are compared structurally by SWR. */
  queryKey: readonly unknown[];
  queryFn: () => Promise<T>;
  /** When false the request is not made and the cache entry stays empty. */
  enabled?: boolean;
  /** Window in which a repeat request is served from cache, in ms. */
  staleTime?: number;
  /** false disables SWR's error retry for this query. */
  retry?: boolean;
}

/** Identity helper that pins the generic — mirrors how queries are declared. */
export function queryOptions<T>(options: QueryOptions<T>): QueryOptions<T> {
  return options;
}

export interface QueryResult<T> {
  data: T | undefined;
  error: unknown;
  /** No data yet and a request is in flight (or waiting to be enabled). */
  isPending: boolean;
  isLoading: boolean;
  /** A request is in flight, including background revalidation. */
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => Promise<{ data: T | undefined; error: unknown }>;
}

export function useQuery<T>(options: QueryOptions<T>): QueryResult<T> {
  const { queryKey, queryFn, enabled = true, staleTime = 0, retry = true } = options;

  // A null key tells SWR not to fetch — this is how `enabled` is expressed.
  const key = enabled ? queryKey : null;

  // Keep the latest queryFn without making it part of the cache key.
  const fnRef = useRef(queryFn);
  fnRef.current = queryFn;

  const config: SWRConfiguration<T> = {
    dedupingInterval: staleTime,
    shouldRetryOnError: retry,
    revalidateOnFocus: false,
    // Deliberately NOT keepPreviousData. Every query key here embeds the signed
    // in uid, so holding the last key's data across a key change would render
    // the previous account's records while the new one loads.
    keepPreviousData: false,
  };

  const { data, error, isLoading, isValidating, mutate } = useSWR<T>(
    key,
    () => fnRef.current(),
    config,
  );

  const refetch = useCallback(async () => {
    try {
      // throwOnError:false would swallow it into undefined; catching keeps the
      // error available to callers that branch on result.error.
      const next = await mutate();
      return { data: next, error: null as unknown };
    } catch (err) {
      return { data: undefined, error: err };
    }
  }, [mutate]);

  return {
    data,
    error: error ?? null,
    isPending: enabled && data === undefined && error === undefined,
    isLoading,
    isFetching: isValidating,
    isError: Boolean(error),
    isSuccess: !error && data !== undefined,
    refetch,
  };
}

export interface MutationOptions<TData, TVars> {
  mutationFn: (variables: TVars) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVars) => void;
  onError?: (error: unknown, variables: TVars) => void;
}

export interface MutationResult<TData, TVars> {
  /** Fire and forget; errors are routed to onError, never thrown. */
  mutate: (variables: TVars) => void;
  /** Awaitable; rejects if the mutation fails. */
  mutateAsync: (variables: TVars) => Promise<TData>;
  isPending: boolean;
  error: unknown;
}

export function useMutation<TData, TVars = void>(
  options: MutationOptions<TData, TVars>,
): MutationResult<TData, TVars> {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Read handlers through a ref so callers can pass inline closures without
  // changing the identity of mutate/mutateAsync on every render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutateAsync = useCallback(async (variables: TVars): Promise<TData> => {
    setIsPending(true);
    setError(null);
    try {
      const data = await optionsRef.current.mutationFn(variables);
      optionsRef.current.onSuccess?.(data, variables);
      return data;
    } catch (err) {
      setError(err);
      optionsRef.current.onError?.(err, variables);
      throw err;
    } finally {
      setIsPending(false);
    }
  }, []);

  const mutate = useCallback(
    (variables: TVars) => {
      // onError has already handled it; this catch only stops the unhandled
      // rejection warning.
      void mutateAsync(variables).catch(() => {});
    },
    [mutateAsync],
  );

  return { mutate, mutateAsync, isPending, error };
}

export interface QueryClient {
  /** Revalidates every cached key that starts with the given prefix. */
  invalidateQueries: (filter: { queryKey: readonly unknown[] }) => Promise<unknown>;
  /**
   * Drops every cached entry without revalidating. Used on sign-out so the next
   * account never sees the previous one's data.
   */
  clear: () => Promise<unknown>;
  /**
   * No-op kept for call-site symmetry on sign-out. SWR has no request
   * cancellation; clear() already prevents a late response from being shown,
   * because the discarded cache entry has no subscriber to render into.
   */
  cancelQueries: () => Promise<void>;
}

function matchesPrefix(key: unknown, prefix: readonly unknown[]): boolean {
  return (
    Array.isArray(key) &&
    key.length >= prefix.length &&
    prefix.every((part, i) => Object.is(key[i], part))
  );
}

export function useQueryClient(): QueryClient {
  const { mutate } = useSWRConfig();

  const invalidateQueries = useCallback(
    ({ queryKey }: { queryKey: readonly unknown[] }) =>
      mutate((key: unknown) => matchesPrefix(key, queryKey), undefined, { revalidate: true }),
    [mutate],
  );

  const clear = useCallback(() => mutate(() => true, undefined, { revalidate: false }), [mutate]);

  const cancelQueries = useCallback(() => Promise.resolve(), []);

  return { invalidateQueries, clear, cancelQueries };
}
